import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Redirect, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Visible } from "../../core/components";
import { useEntitlement } from "../../contexts";
import { PageLayout, PageHeader, StatePanel, ScrollToTop } from "../../components/ui";
import MultiProductSearch from "../../components/MultiProductSearch.jsx";
import { useActiveStoreIdM } from "../../core/hooks/useActiveStoreIdM.js";
import CoreListKpiStrip from "../components/CoreListKpiStrip.jsx";
import { deriveStockStateFromRow } from "../components/StockStatePill.jsx";
import { useCoreListTargets } from "../hooks/useCoreListTargets.js";
import { useCoreListActions } from "../hooks/useCoreListActions.js";
import StockActionsPanel from "../components/StockActionsPanel.jsx";
import CoreListTable from "./CoreListTable.jsx";
import CoreListArbitrages from "./CoreListArbitrages.jsx";
import CoreListSuggestions from "../components/CoreListSuggestions.jsx";
import CoreListInsights from "./CoreListInsights.jsx";
import "./core-list-template.scss";
import { HOME_PATH } from "../../core/config/dashboard.js";

// Insights is temporarily withdrawn (Alex, 2026-09-18 — "it keeps failing").
// The route, the template and /api/core-list/insights are all left in place;
// only the way in is removed, so putting it back is this one line.
//
// The Core List no longer reads that endpoint either — see the note in the
// component. Both withdrawals have the same cause and lift together.
// Core List and Ad List are the SAME list of references, split by what kind
// of listing each one has: stock or consignment on the left, merchant-owned on
// the right (Alex, 2026-10-06). Nothing is moved between them by hand — a
// watch taken on consignment appears in Core List because of what it IS, which
// is the only way the two can stay honest.
//
// Arbitrages is not a list at all: it is two analyses of trade-group quotes.
const TABS = [
  { label: "Core List", to: "/stock/core-list" },
  { label: "Ad List", to: "/stock/core-list/ad-list" },
  { label: "Sales Pipeline", to: "/stock/core-list/pipeline" },
  { label: "Arbitrages", to: "/stock/core-list/arbitrages" },
];
const ARBITRAGES_PATH = "/stock/core-list/arbitrages";

// Which list each tab's pending actions come from (Stock Alert, 2026-11-02).
// Arbitrages has none: it is an analysis, not a list.
const ACTION_LIST_BY_PATH = {
  "/stock/core-list": "core",
  "/stock/core-list/ad-list": "ad",
  "/stock/core-list/pipeline": "pipeline",
};

// The store comes from the switcher, per render — see useActiveStoreIdM.
//
// This was a MODULE-LEVEL constant (`getUkStoreIdM()`, 873 in production),
// written when the header had no store selector. One was added to the product
// later and this page was never told, so every store showed — and every
// "+ Add Products" wrote to — DAO Watches UK. Keeping it out of module scope
// is the point: a constant here cannot follow a switch by construction.

// GET /api/core-list?store_id_m=...
// Returns { store_id_m, groups: [{brand, models: [{model, references: [...]}]}] }
const fetchCoreList = async (id, listType) => {
  if (id == null) return { store_id_m: null, groups: [] };
  const { data } = await axios.get(`/api/core-list`, {
    // `list_type` is omitted for the Core List so the request is byte-for-byte
    // what it has always been — the banner and the nav badge send the same
    // one, and the server defaults to 'core'.
    params: { store_id_m: id, ...(listType ? { list_type: listType } : {}) },
  });
  return data || { store_id_m: id, groups: [] };
};

// Legacy deep-link support: the (now-removed) red banner used to link here
// with ?filter=alerting. It used to map to "in-demand", which is withdrawn with
// /api/core-list/insights — so a stale bookmark now lands on the unfiltered
// list rather than selecting a filter that no longer exists and silently
// showing nothing.
const useInitialFilter = () => "all";

const flattenReferences = (groups) => {
  const refs = [];
  (groups || []).forEach((brandGroup) => {
    (brandGroup.models || []).forEach((modelGroup) => {
      (modelGroup.references || []).forEach((ref) => refs.push(ref));
    });
  });
  return refs;
};

const CoreListTemplate = () => {
  const { pathname } = useLocation();
  const [modalOpen, setModalOpen] = useState(false);
  const initialFilter = useInitialFilter();
  const storeIdM = useActiveStoreIdM();
  // Each list is its own set of rows (Guillaume, 2026-10-12): the Ad List is
  // no longer the Core List split by listing ownership, which hid merchant-
  // listed watches from the Core List so adding one looked like it did nothing.
  const listType =
    pathname === "/stock/core-list/pipeline" ? "pipeline"
      : pathname === "/stock/core-list/ad-list" ? "ad"
      : null;
  // The list "+ Add Products" writes to — only the Core List and Ad List tabs
  // have one; the button is hidden elsewhere.
  const addTo =
    pathname === "/stock/core-list" ? "core"
      : pathname === "/stock/core-list/ad-list" ? "ad"
      : null;

  // Two Feature Matrix rows share this page (Alex, 2026-10-27): "Stock alert"
  // is Core List, Ad List and Sales Pipeline; "Geographic Arbitrages &
  // Sequencing" is the Arbitrages tab. Each tab shows with its own row.
  const hasStock = useEntitlement("core_inventory_management");
  const hasArbitrage = useEntitlement("geo_arbitrage");

  // Pending actions for all three lists in one call: the tab pills need every
  // list's count whichever tab is open, and the sidebar badge's total is the
  // same three numbers added up (its own counts-only query, same cache prefix,
  // so marking one done here refreshes both).
  const {
    counts: actionCounts,
    lists: actionLists,
    isLoading: actionsLoading,
    markDone,
    undo: undoDone,
    refresh: refreshActions,
  } = useCoreListActions(storeIdM, { enabled: hasStock });
  const actionList = ACTION_LIST_BY_PATH[pathname] || null;

  const tabs = useMemo(
    () =>
      TABS.filter((tab) => (tab.to === ARBITRAGES_PATH ? hasArbitrage : hasStock)).map((tab) => {
        const list = ACTION_LIST_BY_PATH[tab.to];
        const n = list ? actionCounts[list] || 0 : 0;
        // Red pill only when there is something to do: a "0" on every tab
        // would train the eye to ignore the pill.
        return n > 0
          ? {
              ...tab,
              count: n,
              countTone: "alert",
              countLabel: `${n} pending action${n === 1 ? "" : "s"}`,
            }
          : tab;
      }),
    [hasStock, hasArbitrage, actionCounts]
  );

  const {
    data,
    refetch,
    isFetching,
    isError,
  } = useQuery({
    // The Sales Pipeline is a DIFFERENT list, so it is a different cache
    // entry: sharing one would show the pipeline's rows on the Core List for
    // as long as the cache was warm.
    queryKey: ["core-list", storeIdM, listType],
    queryFn: () => fetchCoreList(storeIdM, listType),
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    staleTime: 0,
    // Without Stock alert this 403s; the store may still be here for Arbitrages.
    enabled: storeIdM != null && hasStock,
  });

  const groups = data?.groups || [];

  // ⚠️ useCoreListDemand is NOT called. It reads /api/core-list/insights, which
  // 500s in production — the same failure that had the Insights tab withdrawn.
  // While it was still called, the Core List paid for it three times over: a
  // SERVER ERROR toast on every page load, five KPI tiles stuck on their
  // loading skeletons forever, and a Demand 30D column reading 0 against all
  // 118 references. Removing the call is what takes the page back to telling
  // the truth.
  //
  // Everything downstream of it is withdrawn with it, in one piece so it comes
  // back in one piece: the Demand 30D column, the Opportunity and Open leads
  // KPI tiles, and the "In demand" filter. Restoring = re-add this hook and
  // undo the three marked blocks (CoreListKpiStrip's TILES, CoreListTable's
  // demand column, CoreListFilterPills' in-demand entry).
  const {
    targetsByItemId,
    referencesAtTarget,
    isLoading: targetsLoading,
    refetchTargets,
  } = useCoreListTargets(storeIdM, hasStock);

  // Force a refetch on mount even if a cached entry exists.
  useEffect(() => {
    if (storeIdM != null && hasStock) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const kpis = useMemo(() => {
    const refs = flattenReferences(groups);
    let outOfStock = 0;

    refs.forEach((ref) => {
      const hasListing = ref.most_recent_listing_id != null;
      const isSold = ref.most_recent_listing_is_sold === true;
      const isActive = ref.most_recent_listing_status === "active";
      // "Out of stock" = no listing at all, or a non-sold listing that's
      // toggled off. Sold references get their own "Sold" pill and aren't
      // double-counted here.
      if (!hasListing || (!isSold && !isActive)) outOfStock += 1;
    });

    // `opportunity` and `openLeads` are gone with the demand hook — both were
    // derived from /api/core-list/insights. See the note above.
    return {
      total: refs.length,
      outOfStock,
      atTarget: referencesAtTarget,
    };
  }, [groups, referencesAtTarget]);

  // Insights' own endpoint doesn't return stock_state — join it from the
  // Core List data we already fetch here, by product_id. References that
  // aren't in the Core List yet have no stock_state anywhere; those render
  // as "—" in Insights rather than a guessed value.
  const stockStateByProductId = useMemo(() => {
    const map = new Map();
    flattenReferences(groups).forEach((ref) => {
      map.set(ref.product_id, deriveStockStateFromRow(ref));
    });
    return map;
  }, [groups]);

  // Suggestions add ONE reference at a time and leave the modal open, so the
  // rep can take two or three in a row. Tracked locally rather than by
  // refetching the list, because a refetch between clicks would make the row
  // they are about to click disappear under the cursor.
  const [suggestedAdded, setSuggestedAdded] = useState([]);

  const addSuggested = async (productId) => {
    if (storeIdM == null || suggestedAdded.includes(productId)) return;
    try {
      await axios.post(`/api/core-list`, {
        store_id_m: storeIdM,
        product_ids: [productId],
      });
      setSuggestedAdded((current) => [...current, productId]);
      refetch();
    } catch (e) {
      console.error("Failed to add suggested reference", e);
      alert("Couldn't add that reference. Please try again.");
    }
  };

  const onConfirmAddProducts = async (selected) => {
    if (storeIdM == null || !selected?.length) {
      setModalOpen(false);
      return;
    }
    try {
      await axios.post(`/api/core-list`, {
        store_id_m: storeIdM,
        product_ids: selected.map((p) => p.id),
        list_type: addTo,
      });
      setModalOpen(false);
      refetch();
    } catch (e) {
      // Surface failure but keep modal open so the user can retry.
      console.error("Failed to add products to Core List", e);
      alert("Failed to add products to Core List. Please try again.");
    }
  };

  const showEmptyStoreMessage = storeIdM == null;
  const activeTab = tabs.find((t) => t.to === pathname) || tabs[0] || TABS[0];

  const actions = useMemo(
    () => addTo == null ? [] : [
      {
        label: addTo === "ad" ? "+ Add to Ad List" : "+ Add to Core List",
        onClick: () => setModalOpen(true),
        variant: "primary",
        size: "lg",
        className: "rc-ui-btn--hero",
        "data-btn": "stock-add",
        disabled: storeIdM == null,
      },
    ],
    // storeIdM belongs here now that it is a per-render value rather than a
    // module constant: without it the memo keeps the FIRST store's `disabled`
    // forever, so the button would stay enabled after switching into a store
    // that has none.
    [storeIdM, addTo]
  );

  // Arbitrages only: the Stock Alert nav lands on /stock/core-list, which
  // this store has no rows for — take it to the tab it does have.
  if (!hasStock && hasArbitrage && pathname !== ARBITRAGES_PATH) {
    return <Redirect to={ARBITRAGES_PATH} />;
  }

  return (
    <PageLayout className="core-list">
      <PageHeader
        title={activeTab.label}
        description="Track must-have inventory and live buyer demand"
        backTo={HOME_PATH}
        tabs={tabs}
        actions={actions}
        activePath={activeTab.to}
      />

      {/* Pending actions at the very top, above the KPI strip (Stock Alert,
          2026-11-02): what needs doing comes before what you have. */}
      {actionList && hasStock && storeIdM != null ? (
        <StockActionsPanel
          actions={actionLists[actionList]}
          isLoading={actionsLoading}
          onMarkDone={markDone}
          onUndo={undoDone}
        />
      ) : null}

      {/* The strip counts Core List references; Arbitrages is the whole
          catalogue, so the three tiles would describe a different list
          (Alex, 2026-10-12). */}
      {pathname !== "/stock/core-list/arbitrages" && (
        <CoreListKpiStrip
          total={kpis.total}
          outOfStock={kpis.outOfStock}
          atTarget={kpis.atTarget}
          isLoading={isFetching || targetsLoading}
        />
      )}

      {/* ── Body ────────────────────────────────────────────────────── */}
      {isError ? (
        // M37: the shared application-state panel replaces this page's hand-rolled banner.
        // Same copy, same Retry — now with alert semantics and a real <Button>.
        <StatePanel
          state="error"
          title="Couldn't load the Core List"
          description="Please try again."
          onRetry={() => refetch()}
        />
      ) : showEmptyStoreMessage ? (
        <StatePanel
          state="empty"
          icon="Store"
          title="No core list yet for this store"
          description="Core List isn't available for this store yet — coming soon."
        />
      ) : (
        <>
          <Visible when={pathname === "/stock/core-list"}>
            <CoreListTable
              groups={groups}
              isFetching={isFetching}
              initialFilter={initialFilter}
              targetsByItemId={targetsByItemId}
              storeIdM={storeIdM}
              onChanged={() => {
                refetch();
                refetchTargets();
                // A listing switched on or off changes what needs doing.
                refreshActions();
              }}
            />
          </Visible>

          {/* Same table, its own list: the references the store advertises. */}
          <Visible when={pathname === "/stock/core-list/ad-list"}>
            <CoreListTable
              groups={groups}
              isFetching={isFetching}
              initialFilter={initialFilter}
              targetsByItemId={targetsByItemId}
              storeIdM={storeIdM}
              onChanged={() => {
                refetch();
                refetchTargets();
                // A listing switched on or off changes what needs doing.
                refreshActions();
              }}
            />
          </Visible>

          {/* The pipeline is the same table of references with the same
              targets — what differs is which list the rows came from, and
              that is decided server-side by list_type. */}
          <Visible when={pathname === "/stock/core-list/pipeline"}>
            <CoreListTable
              groups={groups}
              isFetching={isFetching}
              initialFilter={initialFilter}
              targetsByItemId={targetsByItemId}
              storeIdM={storeIdM}
              onChanged={() => {
                refetch();
                refetchTargets();
                // A listing switched on or off changes what needs doing.
                refreshActions();
              }}
            />
          </Visible>

          <Visible when={pathname === "/stock/core-list/arbitrages"}>
            <CoreListArbitrages storeIdM={storeIdM} />
          </Visible>

          <Visible when={pathname === "/stock/core-list/insights"}>
            <CoreListInsights
              storeIdM={storeIdM}
              stockStateByProductId={stockStateByProductId}
              onAdded={refetch}
            />
          </Visible>
        </>
      )}

      {/* ── Add products modal ──────────────────────────────────────── */}
      {modalOpen && (
        <div className="core-list-modal">
          <div
            className="core-list-modal__backdrop"
            onClick={() => setModalOpen(false)}
          />
          <div className="core-list-modal__panel">
            <div className="core-list-modal__header">
              <span className="core-list-modal__title">
                {addTo === "ad" ? "Add to Ad List" : "Add to Core List"}
              </span>
              <button
                type="button"
                className="core-list-modal__close"
                onClick={() => setModalOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="core-list-modal__body">
              {/* Suggestions sit ABOVE the search: the question "what should
                  I add" comes before "find me this reference", and a store
                  that already knows what it wants scrolls straight past. */}
              {/* Suggestions rank what to STOCK, so they belong to the Core
                  List only; they also add to the Core List. */}
              {addTo === "core" && (
                <CoreListSuggestions
                  storeIdM={storeIdM}
                  addingIds={suggestedAdded}
                  onAdd={addSuggested}
                />
              )}
              <MultiProductSearch
                onConfirm={onConfirmAddProducts}
                onCancel={() => setModalOpen(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Back to top. This page runs to 116 references across ~20 grouped
          tables, so the filter bar ends up a long scroll away. Appears only
          once you have actually scrolled, and sits above the phone tab bar. */}
      <ScrollToTop />
    </PageLayout>
  );
};

export default CoreListTemplate;
